import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { useLocation } from "react-router-dom"
import { persistStore } from "src/persist-store"
import { PATH_ROOT } from "@/routes/paths"
import { useIsDemo } from "@/hooks/common/useIsDemo"
import { useDemoDealsDocuments } from "@/hooks/deals/useDemoDealsDocuments"
import {
  ChatScope,
  createCopyAction,
  createNavigateAction,
  createRatingAction,
  type ChatContextItem,
  type ChatDockAnchor,
  type ChatLayoutMode,
  type ChatPanelSize,
  type ChatSession,
  type Message,
} from "@/types/chat.types"
import {
  getPageContextItem,
  type ContextItem as SelectorContextItem,
} from "@/components/ui/chat-context-selector"

export type ChatPreset = "simple" | "detailed" | "developer"

export interface ChatSettings {
  preset: ChatPreset
  showTimestamps: boolean
  showModelInfo: boolean
  showTokenUsage: boolean
  showScopeBadge: boolean
  showContextSent: boolean
  showConfidence: boolean
  autoPageContext: boolean
  defaultScope: ChatScope
  fontSize: "small" | "medium" | "large"
}

const PRESET_SETTINGS: Record<ChatPreset, Omit<ChatSettings, "preset">> = {
  simple: {
    showTimestamps: false,
    showModelInfo: false,
    showTokenUsage: false,
    showScopeBadge: false,
    showContextSent: false,
    showConfidence: false,
    autoPageContext: true,
    defaultScope: ChatScope.Global,
    fontSize: "medium",
  },
  detailed: {
    showTimestamps: true,
    showModelInfo: true,
    showTokenUsage: false,
    showScopeBadge: true,
    showContextSent: true,
    showConfidence: false,
    autoPageContext: true,
    defaultScope: ChatScope.Global,
    fontSize: "medium",
  },
  developer: {
    showTimestamps: true,
    showModelInfo: true,
    showTokenUsage: true,
    showScopeBadge: true,
    showContextSent: true,
    showConfidence: true,
    autoPageContext: true,
    defaultScope: ChatScope.Global,
    fontSize: "medium",
  },
}

const DEFAULT_SETTINGS: ChatSettings = {
  preset: "simple",
  ...PRESET_SETTINGS.simple,
}

const DEFAULT_LAYOUT = {
  mode: "dock" as ChatLayoutMode,
  dockAnchor: "center" as ChatDockAnchor,
  panelSize: "medium" as ChatPanelSize,
}

const PREFS_STORAGE_KEY = "app-chat-prefs-v1"

interface PersistedChatPrefs {
  settings: ChatSettings
  layout: {
    mode: ChatLayoutMode
    dockAnchor: ChatDockAnchor
    panelSize: ChatPanelSize
  }
}

interface ChatPanelContextType {
  sessions: ChatSession[]
  currentSessionId: string
  currentSession: ChatSession
  messages: Message[]
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  openPanel: () => void
  closePanel: () => void
  togglePanel: () => void
  isCurrentSessionLoading: boolean
  settings: ChatSettings
  updateSettings: (patch: Partial<ChatSettings>) => void
  applyPreset: (preset: ChatPreset) => void
  layoutMode: ChatLayoutMode
  setLayoutMode: (mode: ChatLayoutMode) => void
  dockAnchor: ChatDockAnchor
  setDockAnchor: (anchor: ChatDockAnchor) => void
  panelSize: ChatPanelSize
  setPanelSize: (size: ChatPanelSize) => void
  togglePanelSize: () => void
  currentDraft: string
  setCurrentDraft: (draft: string) => void
  currentScope: ChatScope
  setCurrentScope: (scope: ChatScope) => void
  contextItems: ChatContextItem[]
  setContextItems: (items: ChatContextItem[]) => void
  removeContextItem: (id: string) => void
  dynamicPageItems: ChatContextItem[]
  createSession: (scope?: ChatScope) => string
  selectSession: (sessionId: string) => void
  deleteSession: (sessionId: string) => void
  sendMessage: (overrideText?: string, options?: { scope?: ChatScope }) => void
}

const ChatPanelContext = createContext<ChatPanelContextType | undefined>(undefined)

function createSessionId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function createInitialSession(scope: ChatScope = ChatScope.Global): ChatSession {
  const now = new Date()

  return {
    id: createSessionId(),
    title: "New Chat",
    messages: [],
    scope,
    createdAt: now,
    updatedAt: now,
    draft: "",
    contextItems: [],
  }
}

function generateSessionSummary(session: ChatSession): string {
  if (session.summary) {
    return session.summary
  }

  const firstUserMessage = session.messages.find((message) => message.role === "user")
  if (!firstUserMessage) {
    return "New conversation"
  }

  const preview = firstUserMessage.content.slice(0, 80)
  return preview.length < firstUserMessage.content.length ? `${preview}...` : preview
}

function normaliseContextItem(item: SelectorContextItem | ChatContextItem): ChatContextItem {
  const detail = "detail" in item ? item.detail : undefined
  const scope = "scope" in item ? item.scope : undefined

  return {
    id: item.id,
    type: item.type,
    label: item.label,
    value: item.value,
    description: item.description ?? detail,
    detail: detail ?? item.description,
    scope,
  }
}

function resolveCurrentPageItem(
  pathname: string,
  dynamicPageItems: ChatContextItem[],
): ChatContextItem | null {
  const staticItem = getPageContextItem(pathname)
  if (staticItem) {
    return normaliseContextItem(staticItem)
  }

  const normalised = pathname.replace(/^\/demo/, "")
  const dealMatch = normalised.match(/^\/deal\/(.+)$/)
  if (dealMatch) {
    return (
      dynamicPageItems.find((item) => item.value === `/deal/${dealMatch[1]}`) ?? {
        id: `page-deal-${dealMatch[1]}`,
        type: "page",
        label: "Deal",
        value: `/deal/${dealMatch[1]}`,
        description: "Deal page",
      }
    )
  }

  const analysisMatch = normalised.match(/^\/analysis\/(.+)$/)
  if (analysisMatch) {
    return (
      dynamicPageItems.find((item) => item.value === `/analysis/${analysisMatch[1]}`) ?? {
        id: `page-analysis-${analysisMatch[1]}`,
        type: "page",
        label: "Analysis",
        value: `/analysis/${analysisMatch[1]}`,
        description: "Contract analysis",
      }
    )
  }

  return null
}

function parseCurrencyValue(value: string) {
  const parsed = Number.parseInt(value.replace(/[$,]/g, ""), 10)
  return Number.isFinite(parsed) ? parsed : 0
}

export { PRESET_SETTINGS }

export const useChatPanel = () => {
  const context = useContext(ChatPanelContext)
  if (!context) {
    throw new Error("useChatPanel must be used within a ChatPanelProvider")
  }
  return context
}

export const ChatPanelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation()
  const isDemo = useIsDemo()
  const {
    demoDeals,
    demoContracts: demoContractsData,
    demoContractIds,
  } = useDemoDealsDocuments()

  const initialStateRef = React.useRef<{ sessions: ChatSession[]; currentSessionId: string } | null>(null)
  if (!initialStateRef.current) {
    const initialSession = createInitialSession()
    initialStateRef.current = {
      sessions: [initialSession],
      currentSessionId: initialSession.id,
    }
  }

  const [sessions, setSessions] = useState<ChatSession[]>(initialStateRef.current.sessions)
  const [currentSessionId, setCurrentSessionId] = useState<string>(initialStateRef.current.currentSessionId)
  const [isOpen, setIsOpen] = useState(false)
  const [loadingSessionIds, setLoadingSessionIds] = useState<string[]>([])
  const [settings, setSettings] = useState<ChatSettings>(DEFAULT_SETTINGS)
  const [layoutMode, setLayoutModeState] = useState<ChatLayoutMode>(DEFAULT_LAYOUT.mode)
  const [dockAnchor, setDockAnchorState] = useState<ChatDockAnchor>(DEFAULT_LAYOUT.dockAnchor)
  const [panelSize, setPanelSizeState] = useState<ChatPanelSize>(DEFAULT_LAYOUT.panelSize)
  const [hasHydratedPrefs, setHasHydratedPrefs] = useState(false)

  const dynamicPageItems = useMemo<ChatContextItem[]>(() => [
    ...demoDeals.map((deal) => ({
      id: `page-deal-${deal.id}`,
      type: "page" as const,
      label: deal.name,
      value: `/deal/${deal.id}`,
      description: `${deal.type} · ${deal.value}`,
    })),
    ...demoContractsData.map((contract) => ({
      id: `page-analysis-${contract.contractId}`,
      type: "page" as const,
      label: contract.name,
      value: `/analysis/${contract.contractId}`,
      description: `Analysis · ${contract.type}`,
    })),
  ], [demoContractsData, demoDeals])

  const currentSession = useMemo(() => {
    return sessions.find((session) => session.id === currentSessionId) ?? sessions[0]
  }, [currentSessionId, sessions])

  useEffect(() => {
    if (!currentSession) {
      return
    }

    if (currentSession.id !== currentSessionId) {
      setCurrentSessionId(currentSession.id)
    }
  }, [currentSession, currentSessionId])

  const updateSession = useCallback((sessionId: string, updater: (session: ChatSession) => ChatSession) => {
    setSessions((previousSessions) =>
      previousSessions.map((session) => (
        session.id === sessionId ? updater(session) : session
      ))
    )
  }, [])

  useEffect(() => {
    let isMounted = true

    const loadPrefs = async () => {
      try {
        const storedPrefs = await persistStore.getLocal<PersistedChatPrefs>(PREFS_STORAGE_KEY)
        if (!isMounted || !storedPrefs) {
          return
        }

        setSettings({ ...DEFAULT_SETTINGS, ...storedPrefs.settings })
        setLayoutModeState(storedPrefs.layout.mode)
        setDockAnchorState(storedPrefs.layout.dockAnchor)
        setPanelSizeState(storedPrefs.layout.panelSize)
      } catch (error) {
        console.error("Error loading chat preferences:", error)
      } finally {
        if (isMounted) {
          setHasHydratedPrefs(true)
        }
      }
    }

    loadPrefs()

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    if (!hasHydratedPrefs) {
      return
    }

    persistStore.putLocal<PersistedChatPrefs>(
      PREFS_STORAGE_KEY,
      {
        settings,
        layout: {
          mode: layoutMode,
          dockAnchor,
          panelSize,
        },
      },
      { persistOnLogout: true },
    ).catch((error) => {
      console.error("Error saving chat preferences:", error)
    })
  }, [dockAnchor, hasHydratedPrefs, layoutMode, panelSize, settings])

  useEffect(() => {
    if (!currentSession) {
      return
    }

    if (!settings.autoPageContext) {
      updateSession(currentSession.id, (session) => ({
        ...session,
        contextItems: session.contextItems.filter((item) => item.type !== "page"),
      }))
      return
    }

    const pageItem = resolveCurrentPageItem(location.pathname, dynamicPageItems)
    if (!pageItem) {
      return
    }

    updateSession(currentSession.id, (session) => {
      const withoutPageItems = session.contextItems.filter((item) => item.type !== "page")
      return {
        ...session,
        contextItems: [pageItem, ...withoutPageItems],
      }
    })
  }, [currentSession?.id, dynamicPageItems, location.pathname, settings.autoPageContext, updateSession])

  const openPanel = useCallback(() => setIsOpen(true), [])
  const closePanel = useCallback(() => setIsOpen(false), [])
  const setPanelOpen = useCallback((open: boolean) => setIsOpen(open), [])
  const togglePanel = useCallback(() => setIsOpen((currentOpen) => !currentOpen), [])

  const updateSettings = useCallback((patch: Partial<ChatSettings>) => {
    setSettings((currentSettings) => {
      const nextSettings = {
        ...currentSettings,
        ...patch,
        preset: patch.preset ?? "simple",
      }

      return nextSettings
    })
  }, [])

  const applyPreset = useCallback((preset: ChatPreset) => {
    setSettings({
      preset,
      ...PRESET_SETTINGS[preset],
    })
  }, [])

  const setLayoutMode = useCallback((mode: ChatLayoutMode) => {
    setLayoutModeState(mode)
    setIsOpen(true)
  }, [])

  const setDockAnchor = useCallback((anchor: ChatDockAnchor) => {
    setDockAnchorState(anchor)
  }, [])

  const setPanelSize = useCallback((size: ChatPanelSize) => {
    setPanelSizeState(size)
  }, [])

  const togglePanelSize = useCallback(() => {
    setPanelSizeState((currentSize) => currentSize === "medium" ? "large" : "medium")
  }, [])

  const setCurrentDraft = useCallback((draft: string) => {
    if (!currentSession) {
      return
    }

    updateSession(currentSession.id, (session) => ({
      ...session,
      draft,
    }))
  }, [currentSession, updateSession])

  const setCurrentScope = useCallback((scope: ChatScope) => {
    if (!currentSession) {
      return
    }

    updateSession(currentSession.id, (session) => ({
      ...session,
      scope,
    }))
  }, [currentSession, updateSession])

  const setContextItems = useCallback((items: ChatContextItem[]) => {
    if (!currentSession) {
      return
    }

    updateSession(currentSession.id, (session) => ({
      ...session,
      contextItems: items.map(normaliseContextItem),
    }))
  }, [currentSession, updateSession])

  const removeContextItem = useCallback((id: string) => {
    if (!currentSession) {
      return
    }

    updateSession(currentSession.id, (session) => ({
      ...session,
      contextItems: session.contextItems.filter((item) => item.id !== id),
    }))
  }, [currentSession, updateSession])

  const createSession = useCallback((scope?: ChatScope) => {
    const nextSession = createInitialSession(scope ?? settings.defaultScope)
    setSessions((previousSessions) => [nextSession, ...previousSessions])
    setCurrentSessionId(nextSession.id)
    return nextSession.id
  }, [settings.defaultScope])

  const selectSession = useCallback((sessionId: string) => {
    setCurrentSessionId(sessionId)
  }, [])

  const deleteSession = useCallback((sessionId: string) => {
    setSessions((previousSessions) => {
      const remainingSessions = previousSessions.filter((session) => session.id !== sessionId)
      const fallbackSession = remainingSessions[0] ?? createInitialSession(settings.defaultScope)
      const nextSessions = remainingSessions.length > 0 ? remainingSessions : [fallbackSession]

      setCurrentSessionId((activeSessionId) => (
        activeSessionId === sessionId ? nextSessions[0].id : activeSessionId
      ))

      return nextSessions
    })
  }, [settings.defaultScope])

  const buildMockResponse = useCallback((
    userInput: string,
    messageId: string,
    scope: ChatScope,
    contextItems: ChatContextItem[],
  ): Message => {
    const paths = isDemo ? PATH_ROOT.demo : PATH_ROOT.main

    if (contextItems.length > 0) {
      const firstItem = contextItems[0]

      if (firstItem.type === "contract" || firstItem.type === "document") {
        const contract = demoContractsData.find((item) => item.contractId === firstItem.id)
        if (contract) {
          const responseContent = [
            `Analyzing "${contract.name}" (${contract.type}).`,
            `Status: ${contract.status}.`,
            `Shared with ${contract.sharedWith.length} collaborator(s).`,
            "I can break down key clauses, flag risks, or compare it against another agreement.",
          ].join(" ")

          return {
            id: messageId,
            role: "assistant",
            content: responseContent,
            timestamp: new Date(),
            scope,
            actions: [
              createNavigateAction("View Analysis", `${paths.analysis}/${contract.contractId}`),
              createRatingAction(messageId),
              createCopyAction(responseContent),
            ],
          }
        }
      }

      if (firstItem.type === "page") {
        const responseContent = [
          `I can help with the "${firstItem.label}" page.`,
          firstItem.description ? `${firstItem.description}.` : "",
          `Ask about tasks, summaries, or next steps for "${firstItem.label}".`,
        ].filter(Boolean).join(" ")

        return {
          id: messageId,
          role: "assistant",
          content: responseContent,
          timestamp: new Date(),
          scope,
          actions: [
            createNavigateAction("Open Page", `${isDemo ? "/demo" : ""}${firstItem.value}`),
            createRatingAction(messageId),
            createCopyAction(responseContent),
          ],
        }
      }
    }

    const activeDeals = demoDeals.filter((deal) => deal.status === "In Progress")
    const totalDealValue = demoDeals.reduce((sum, deal) => sum + parseCurrencyValue(deal.value), 0)
    const averageDealValue = demoDeals.length > 0 ? Math.round(totalDealValue / demoDeals.length) : 0

    const genericResponses: Array<{ content: string; actions: Message["actions"] }> = [
      {
        content: `I found ${demoContractsData.length} contracts in your vault. "${demoContractsData[0]?.name ?? "your latest contract"}" is a good place to start if you want clause-level analysis.`,
        actions: demoContractIds.ARTIST_RECORDING_DEAL
          ? [
              createNavigateAction("View Contract", `${paths.analysis}/${demoContractIds.ARTIST_RECORDING_DEAL}`),
              createNavigateAction("Open Vault", paths.vault),
            ]
          : undefined,
      },
      {
        content: `Based on your ${demoDeals.length} demo deals, the average deal value is $${averageDealValue.toLocaleString()}. You currently have ${activeDeals.length} active deals in progress.`,
        actions: [
          createNavigateAction("Open Dashboard", paths.dashboard),
          createNavigateAction("Deal Pipeline", paths.dealPipeline),
        ],
      },
      {
        content: `You can ask me to summarize a contract, compare two deals, or walk through deliverables. "${userInput}" is a good start, and I can get more specific if you add context.`,
        actions: [
          createNavigateAction("Open Chat", paths.chat),
        ],
      },
    ]

    const selectedResponse = genericResponses[Math.floor(Math.random() * genericResponses.length)]
    return {
      id: messageId,
      role: "assistant",
      content: selectedResponse.content,
      timestamp: new Date(),
      scope,
      actions: [
        ...(selectedResponse.actions ?? []),
        createRatingAction(messageId),
        createCopyAction(selectedResponse.content),
      ],
    }
  }, [demoContractIds, demoContractsData, demoDeals, isDemo])

  const sendMessage = useCallback((overrideText?: string, options?: { scope?: ChatScope }) => {
    if (!currentSession) {
      return
    }

    const content = (overrideText ?? currentSession.draft).trim()
    if (!content || loadingSessionIds.includes(currentSession.id)) {
      return
    }

    const sessionId = currentSession.id
    const userMessageId = createSessionId()
    const assistantMessageId = createSessionId()
    const contextItems = currentSession.contextItems.map(normaliseContextItem)
    const scope = options?.scope ?? currentSession.scope

    const userMessage: Message = {
      id: userMessageId,
      role: "user",
      content,
      timestamp: new Date(),
      scope,
      contextItems,
    }

    openPanel()
    updateSession(sessionId, (session) => {
      const nextMessages = [...session.messages, userMessage]
      return {
        ...session,
        title: session.messages.length === 0
          ? content.slice(0, 50) + (content.length > 50 ? "..." : "")
          : session.title,
        summary: generateSessionSummary({
          ...session,
          messages: nextMessages,
        }),
        messages: nextMessages,
        scope,
        draft: "",
        updatedAt: new Date(),
      }
    })
    setLoadingSessionIds((currentIds) => [...currentIds, sessionId])

    window.setTimeout(() => {
      const assistantMessage = buildMockResponse(content, assistantMessageId, scope, contextItems)
      updateSession(sessionId, (session) => ({
        ...session,
        summary: generateSessionSummary(session),
        messages: [...session.messages, assistantMessage],
        updatedAt: new Date(),
      }))
      setLoadingSessionIds((currentIds) => currentIds.filter((id) => id !== sessionId))
    }, 900)
  }, [buildMockResponse, currentSession, loadingSessionIds, openPanel, updateSession])

  const value: ChatPanelContextType | undefined = currentSession
    ? {
        sessions,
        currentSessionId,
        currentSession,
        messages: currentSession.messages,
        isOpen,
        setIsOpen: setPanelOpen,
        openPanel,
        closePanel,
        togglePanel,
        isCurrentSessionLoading: loadingSessionIds.includes(currentSession.id),
        settings,
        updateSettings,
        applyPreset,
        layoutMode,
        setLayoutMode,
        dockAnchor,
        setDockAnchor,
        panelSize,
        setPanelSize,
        togglePanelSize,
        currentDraft: currentSession.draft,
        setCurrentDraft,
        currentScope: currentSession.scope,
        setCurrentScope,
        contextItems: currentSession.contextItems,
        setContextItems,
        removeContextItem,
        dynamicPageItems,
        createSession,
        selectSession,
        deleteSession,
        sendMessage,
      }
    : undefined

  if (!value) {
    return null
  }

  return (
    <ChatPanelContext.Provider value={value}>
      {children}
    </ChatPanelContext.Provider>
  )
}
